// ASSISTENTE: o chat com IA da Home e da tela /support/assistant, mais as
// PERGUNTAS FREQUENTES que alimentam a Ajuda.
//
// POR QUE o FAQ mora aqui e não num módulo de ajuda: ele vem de
// /v1/assistant/faq porque é o MESMO texto que alimenta o prompt do assistente
// no back. Duplicá-lo no front faria a tela e a IA divergirem com o tempo — e
// separá-lo daqui esconderia justamente esse parentesco.
//
// O que NÃO está aqui: o chamado de suporte (./support.js). A Ajuda e o
// assistente oferecem "abrir chamado" quando a IA não resolve, mas isso é outra
// fronteira (o Jira) e as duas metades só se encontram na tela.
//
// `streamAssistantChat` é a única função do front que consome SSE: o `api()`
// resolve o corpo inteiro, e aqui é preciso pintar cada token e cada execução
// de tool conforme chegam. Daí o `fetch` cru com o reader.
import { api, API_BASE_URL, toApiError } from "./client.js";
import i18n from "../i18n.js";

export interface AssistantChatMessage {
  role: "user" | "assistant";
  content: string;
}

export type AssistantChatEvent =
  | { type: "conversation"; id: string; title: string }
  | { type: "delta"; content: string }
  | { type: "tool_start"; id: string; name: string }
  | { type: "tool_result"; id: string; name: string; ok: boolean }
  | { type: "done" }
  | { type: "error"; message: string };

export interface AssistantToolUsage {
  name: string;
  ok: boolean;
}

export interface AssistantConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface AssistantConversation {
  id: string;
  title: string;
  messages: Array<{
    id: string;
    role: "user" | "assistant";
    content: string;
    tools: AssistantToolUsage[];
  }>;
}

export interface FaqEntry {
  id: string;
  category: string;
  question: string;
  answer: string;
}

export interface Faq {
  categories: string[];
  entries: FaqEntry[];
}

// GET /v1/assistant/faq — as perguntas frequentes vêm do back porque são o
// MESMO texto que alimenta o prompt do assistente. Duplicar aqui faria a tela
// e a IA divergirem com o tempo.
export async function getFaq(): Promise<Faq> {
  const { data, error } = await api<Faq>("/v1/assistant/faq");
  if (error) throw toApiError("GET /v1/assistant/faq", error);
  return data;
}

// Conversas salvas do assistente. Todas privadas do par usuário + marca ativa:
// o back filtra pelos dois e responde 404 para conversa alheia.
export async function listAssistantConversations(): Promise<AssistantConversationSummary[]> {
  const { data, error } = await api<AssistantConversationSummary[]>("/v1/assistant/conversations");
  if (error) throw toApiError("GET /v1/assistant/conversations", error);
  return data;
}

export async function getAssistantConversation(id: string): Promise<AssistantConversation> {
  const { data, error } = await api<AssistantConversation>(`/v1/assistant/conversations/${id}`);
  if (error) throw toApiError(`GET /v1/assistant/conversations/${id}`, error);
  return data;
}

export async function renameAssistantConversation(id: string, title: string): Promise<void> {
  const { error } = await api(`/v1/assistant/conversations/${id}`, {
    method: "PATCH",
    body: { title },
  });
  if (error) throw toApiError(`PATCH /v1/assistant/conversations/${id}`, error);
}

export async function deleteAssistantConversation(id: string): Promise<void> {
  const { error } = await api(`/v1/assistant/conversations/${id}`, { method: "DELETE" });
  if (error) throw toApiError(`DELETE /v1/assistant/conversations/${id}`, error);
}

// POST /v1/assistant/chat — fetch direto porque better-fetch resolve o corpo
// inteiro; aqui precisamos consumir o SSE incrementalmente para pintar cada
// token e cada execução de tool na home.
//
// O histórico NÃO vai mais no corpo: o back o carrega pelo conversationId. Sem
// conversationId, o primeiro evento do stream é o `conversation` com o id da
// conversa recém-criada — é por ele que a tela fixa a URL antes de o texto
// começar a chegar.
export interface AssistantChatRequest {
  conversationId?: string;
  message: string;
}

export async function streamAssistantChat(
  request: AssistantChatRequest,
  onEvent: (event: AssistantChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/v1/assistant/chat`, {
    method: "POST",
    headers: { Accept: "text/event-stream", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(request),
    signal,
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(
      payload?.error || i18n.t("apiErrors.assistantStatus", { status: response.status }),
    );
  }
  if (!response.body) throw new Error(i18n.t("apiErrors.assistantEmpty"));

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { value, done } = await reader.read();
    buffer += decoder.decode(value, { stream: !done });
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() ?? "";

    for (const block of blocks) {
      const data = block
        .split(/\r?\n/)
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart())
        .join("\n");
      if (!data) continue;
      onEvent(JSON.parse(data) as AssistantChatEvent);
    }
    if (done) break;
  }
}
